// screen: Settings — tenant settings in tabs: General, Branding, Defaults, Recording / Catch-up, Billing (usage).
// General = trash, subtitles, images, lip-sync; Branding/Defaults/Billing = change tenant-settings-sections (billing:read).
import * as React from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useLingui } from '@lingui/react/macro';
import { Button, Input, Skeleton, Switch, Tabs, TabsContent, TabsList, TabsTrigger, useToast } from '@viewstream/ui';
import { AuditLog } from '~/app/AdminAudit';
import { TenantStatementsSection } from '~/app/BillingStatements';
import { appTitle } from '~/app/brand';
import { useTenant } from '~/app/tenant';
import { can } from '~/app/permissions';
import { problemText } from '~/app/team';
import { useTrash, useTrashRetention } from '~/app/trash';
import { isLipsyncAvailable, LIPSYNC_DEFAULT_INTERVAL, LIPSYNC_DEFAULT_THRESHOLD, lipsyncBody, supportsPause, supportsThreshold, useLipsyncTenant, usePutLipsyncTenant, type LipsyncBody } from '~/app/lipsync';
import { EpisodeRule, IntervalSelect, LipSyncCost, LipSyncUnavailable, ThresholdField } from '~/app/LipSync';
import { SubtitleSettingsSection } from '~/app/Subtitles';
import { SummarySettingsSection } from '~/app/ContentSummary';
import { NumbersSettingsSection } from '~/app/NumbersCard';
import { AutoPublishSettingsSection } from '~/app/AIReview';
import { RecommendationsSettingsSection } from '~/app/Recommendations';
import { ViewerAccountsSettingsSection } from '~/app/ViewerAccounts';
import { ArchiveSearchSettingsSection } from '~/app/ArchiveSearch';
import { ContextualAdsSettingsSection } from '~/app/ContextualAds';
import { BrandingSection } from '~/app/Branding';
import { TenantDefaultsSection } from '~/app/TenantDefaults';
import { CatchupExclusionsSection } from '~/app/CatchupExclusions';
import { BillingUsageSection } from '~/app/BillingUsage';
import { PlanBillingSection } from '~/app/Plan';
import { ModeToggle, useUiMode } from '~/app/uiMode';
import { useImageSettings, usePutImageSettings } from '~/app/upscale';
import { TenantAnalyticsSection } from '~/app/TenantAnalytics';

export const Route = createFileRoute('/_layout/t/$slug/settings')({ component: Screen });

function Screen() {
  const { t } = useLingui();
  const { tenant, scopes } = useTenant();
  const toast = useToast();
  React.useEffect(() => { document.title = appTitle(t`Settings`); }, [t]);
  const platform = tenant.mode !== 'cdn';
  const [mode] = useUiMode();
  const trash = useTrash(tenant.slug, platform && can(scopes, 'assets:read'));
  const save = useTrashRetention(tenant.slug);
  const canEdit = can(scopes, 'assets:purge');
  const [days, setDays] = React.useState('');
  React.useEffect(() => { if (trash.data) setDays(String(trash.data.retention_days)); }, [trash.data]);
  const n = Number(days);
  const valid = Number.isInteger(n) && n >= 1 && n <= 365;
  const tabs = ['general', 'branding', ...(platform ? ['defaults', 'catchup'] : []), ...(can(scopes, 'billing:read') ? ['billing'] : []), ...(can(scopes, 'team:manage') ? ['audit'] : [])];
  const [tab, setTabState] = React.useState(() => {
    const x = new URLSearchParams(window.location.search).get('tab') ?? 'general';
    return tabs.includes(x) ? x : 'general';
  });
  const setTab = React.useCallback((v: string) => {
    setTabState(v);
    try { const u = new URL(window.location.href); if (v === 'general') u.searchParams.delete('tab'); else u.searchParams.set('tab', v); window.history.replaceState(window.history.state, '', u); } catch { /* ignore */ }
  }, []);
  const openGeneral = React.useCallback((anchor: string) => {
    setTab('general');
    window.setTimeout(() => {
      const el = document.getElementById(anchor);
      if (!el) return;
      el.setAttribute('tabindex', '-1');
      el.scrollIntoView({ block: 'start' });
      el.focus({ preventScroll: true });
    }, 50);
  }, [setTab]);
  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <h1 className="text-2xl font-semibold">{t`Settings`}</h1>
        {tab === 'general' ? <ModeToggle /> : null}
      </div>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="overflow-x-auto" aria-label={t`Settings sections`}>
          <TabsTrigger value="general" data-testid="tab-general">{t`General`}</TabsTrigger>
          <TabsTrigger value="branding" data-testid="tab-branding">{t`Branding`}</TabsTrigger>
          {platform ? <TabsTrigger value="defaults" data-testid="tab-defaults">{t`Defaults`}</TabsTrigger> : null}
          {platform ? <TabsTrigger value="catchup" data-testid="tab-catchup">{t`Recording / Catch-up`}</TabsTrigger> : null}
          {can(scopes, 'billing:read') ? <TabsTrigger value="billing" data-testid="tab-billing">{t`Billing`}</TabsTrigger> : null}
          {can(scopes, 'team:manage') ? <TabsTrigger value="audit" data-testid="tab-audit">{t`Audit log`}</TabsTrigger> : null}
        </TabsList>
        <TabsContent value="general" className="space-y-6 pt-4">
          <TenantAnalyticsSection />
          {platform ? (
            <section className="max-w-2xl rounded-lg border border-border p-4" aria-labelledby="settings-trash">
              <h2 id="settings-trash" className="text-lg font-semibold">{t`Library trash`}</h2>
              <p className="mt-1 text-sm text-fg-muted">{t`Assets moved to the trash keep their master and can be restored. After the retention period they are deleted permanently, together with their uploaded source and CDN copies.`}</p>
              {trash.isLoading ? <Skeleton className="mt-3 h-10 w-48" /> : (
                <form className="mt-3 flex flex-wrap items-end gap-3" onSubmit={(e) => {
                  e.preventDefault();
                  if (!valid) return;
                  save.mutate(n, {
                    onSuccess: () => toast.push({ title: t`Saved`, tone: 'ok' }),
                    onError: (err) => toast.push({ title: t`Could not save`, description: problemText(err), tone: 'danger' }),
                  });
                }}>
                  <label className="text-sm" htmlFor="trash-days">
                    <span className="mb-1 block">{t`Delete permanently after (days)`}</span>
                    <Input id="trash-days" type="number" inputMode="numeric" min={1} max={365} className="w-32" value={days} disabled={!canEdit}
                      aria-invalid={!valid || undefined} onChange={(e) => setDays(e.target.value)} data-testid="trash-days" />
                  </label>
                  {canEdit ? <Button type="submit" disabled={!valid || save.isPending}>{t`Save`}</Button> : <span className="text-xs text-fg-muted">{t`Only admins and owners can change this.`}</span>}
                  <Link to={'/t/$slug/library/trash' as never} params={{ slug: tenant.slug } as never} className="text-sm text-primary underline-offset-4 hover:underline">{t`Open the trash`}</Link>
                </form>
              )}
            </section>
          ) : null}
          {platform ? <SubtitleSettingsSection /> : null}
          {platform ? <SummarySettingsSection /> : null}
          {platform ? <NumbersSettingsSection /> : null}
          {platform ? <AutoPublishSettingsSection /> : null}
          {platform ? <RecommendationsSettingsSection /> : null}
          {platform ? <ViewerAccountsSettingsSection /> : null}
          {platform ? <ArchiveSearchSettingsSection /> : null}
          {platform ? <ContextualAdsSettingsSection /> : null}
          {platform ? <ImageSettingsSection /> : null}
          {platform && mode === 'advanced' ? <LipSyncDefaults /> : null}
          {platform ? <p className="text-sm text-fg-muted">{t`Video packaging, the default player, recording and publishing defaults are under Defaults.`}</p> : null}
        </TabsContent>
        <TabsContent value="branding" className="pt-4"><BrandingSection /></TabsContent>
        {platform ? <TabsContent value="defaults" className="pt-4"><TenantDefaultsSection onOpenGeneral={openGeneral} /></TabsContent> : null}
        {platform ? <TabsContent value="catchup" className="pt-4"><CatchupExclusionsSection /></TabsContent> : null}
        {can(scopes, 'billing:read') ? <TabsContent value="billing" className="space-y-8 pt-4"><PlanBillingSection tenant={tenant.slug} canPay={can(scopes, 'tenant:settings')} /><BillingUsageSection /><TenantStatementsSection tenant={tenant.slug} /></TabsContent> : null}
        {/* change admin-p6: the tenant's audit (Admin+), Interhost operator changes included */}
        {can(scopes, 'team:manage') ? <TabsContent value="audit" className="pt-4"><AuditLog scope="tenant" tenantSlug={tenant.slug} /></TabsContent> : null}
      </Tabs>
    </div>
  );
}

/** "שיפור תמונות אוטומטי (AI)" — automatic AI upscaling of small images (change ai-image-upscale). Off by default. */
function ImageSettingsSection() {
  const { t } = useLingui();
  const { scopes } = useTenant();
  const toast = useToast();
  const q = useImageSettings(can(scopes, 'assets:read'));
  const put = usePutImageSettings();
  const canEdit = can(scopes, 'tenant:settings');
  if (q.isError) return null; // feature not deployed yet
  const on = !!q.data?.auto_upscale;
  const set = (v: boolean) => put.mutate({ auto_upscale: v }, {
    onSuccess: () => toast.push({ title: t`Saved`, tone: 'ok' }),
    onError: (err) => toast.push({ title: t`Could not save`, description: problemText(err), tone: 'danger' }),
  });
  return (
    <section className="max-w-2xl space-y-2 rounded-lg border border-border p-4" aria-labelledby="settings-images" data-testid="image-settings">
      <h2 id="settings-images" className="text-lg font-semibold">{t`Images`}</h2>
      {q.isLoading ? <Skeleton className="h-16" /> : (
        <>
          <div className="flex items-center justify-between gap-3">
            <label htmlFor="images-auto-upscale" className="font-medium">{t`Automatic image enhancement (AI)`}</label>
            <Switch id="images-auto-upscale" checked={on} disabled={!canEdit || put.isPending} onCheckedChange={set} data-testid="images-auto-upscale" />
          </div>
          <p className="text-sm text-fg-muted">{t`Small posters, imported artwork and stills from recordings are enlarged with an AI upscaler on our GPU server in Israel so they stay sharp on big tiles and TVs (up to 2560 px wide). Only images smaller than needed are processed. Faces are never retouched and the original is always kept: each image can be compared and reverted in its poster panel. Off by default.`}</p>
          {!canEdit ? <p className="text-xs text-fg-muted">{t`Only owners can change this.`}</p> : null}
        </>
      )}
    </section>
  );
}

/** Tenant defaults of the lip-sync monitor and the automatic correction of recordings (change lipsync-monitor). */
function LipSyncDefaults() {
  const { t } = useLingui();
  const { scopes } = useTenant();
  const toast = useToast();
  const q = useLipsyncTenant();
  const put = usePutLipsyncTenant();
  const canEdit = can(scopes, 'tenant:settings');
  if (q.isError) return null; // feature not deployed yet
  if (q.data && !isLipsyncAvailable(q.data)) return <LipSyncUnavailable />; // ViewStream turned lip-sync off (LIPSYNC_ENABLED)
  const cur = q.data ?? { monitor: null, correct: null, interval_min: null, measure_at: null };
  const body = (b: Partial<LipsyncBody>): LipsyncBody => lipsyncBody(cur, b, true);
  const set = (b: Partial<LipsyncBody>) =>
    put.mutate(body(b), { onSuccess: () => toast.push({ title: t`Saved`, tone: 'ok' }), onError: (err) => toast.push({ title: t`Could not save`, description: problemText(err), tone: 'danger' }) });
  const busy = !canEdit || put.isPending;
  const interval = cur.interval_min ?? LIPSYNC_DEFAULT_INTERVAL;
  const threshold = cur.threshold_ms ?? LIPSYNC_DEFAULT_THRESHOLD;
  return (
    <section className="max-w-2xl space-y-4 rounded-lg border border-border p-4" aria-labelledby="settings-lipsync" data-testid="lipsync-defaults">
      <h2 id="settings-lipsync" className="text-lg font-semibold">{t`Lip-sync monitor`}</h2>
      <p className="text-sm text-fg-muted">{t`Defaults for every channel; a channel can override them in its Settings or Health tab.`}</p>
      {q.isLoading ? <Skeleton className="h-24" /> : (
        <>
          <div className="space-y-2">
            <div className="flex items-center justify-between gap-3">
              <label htmlFor="lipsync-monitor" className="font-medium">{t`Monitoring`}</label>
              <Switch id="lipsync-monitor" checked={!!cur.monitor} disabled={busy} onCheckedChange={(v) => set({ monitor: v })} data-testid="lipsync-monitor" />
            </div>
            <p className="text-sm text-fg-muted">{t`Scores a 10-second clip of each channel's recording on our GPU server in Israel (never the live stream) and alerts when the source's sound and lips drift more than ${threshold} ms apart. Off by default.`}</p>
            {supportsThreshold(cur) ? (
              <ThresholdField id="lipsync-threshold" value={cur.threshold_ms ?? null} inherited={LIPSYNC_DEFAULT_THRESHOLD} disabled={busy} onSave={(v) => set({ threshold_ms: v })} />
            ) : null}
            <EpisodeRule threshold={threshold} />
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-sm">{t`Sample`}</span>
              <div className="w-56"><IntervalSelect value={interval} disabled={busy} onChange={(v) => set({ interval_min: v })} /></div>
            </div>
            <LipSyncCost intervalMin={interval} />
          </div>
          <div className="space-y-2 border-t border-border pt-4">
            <div className="flex items-center justify-between gap-3">
              <label htmlFor="lipsync-correct" className="font-medium">{t`Automatic correction of recordings`}</label>
              <Switch id="lipsync-correct" checked={!!cur.correct} disabled={busy} onCheckedChange={(v) => set({ correct: v })} data-testid="lipsync-correct" />
            </div>
            <p className="text-sm text-fg-muted">{t`Where monitoring finds a sustained offset (60–800 ms), catch-up, start-over, clips and programmes published as VOD play the recorded audio shifted back in sync. The original recording is kept and live is never changed. Needs monitoring. Off by default.`}</p>
          </div>
          {supportsPause(cur) ? (
            <div className="space-y-2 border-t border-border pt-4">
              <div className="flex items-center justify-between gap-3">
                <label htmlFor="lipsync-pause" className="font-medium">{t`Pause during excluded programmes`}</label>
                <Switch id="lipsync-pause" checked={cur.pause_on_excluded !== false} disabled={busy} onCheckedChange={(v) => set({ pause_on_excluded: v })} data-testid="lipsync-pause" />
              </div>
              <p className="text-sm text-fg-muted">{t`While a programme excluded from catch-up is on air (for example the Shabbat still or the silent wave), no lip-sync samples are taken and the monitor shows "Paused" instead of raising an alert. It resumes by itself when the programme ends. Which programmes are excluded is set under Catch-up exclusions; this switch does not change catch-up. On by default.`}</p>
            </div>
          ) : null}
        </>
      )}
    </section>
  );
}
